import groupBy from 'lodash/groupBy';
import PropTypes from 'prop-types';
import type { ComponentType, ReactNode } from 'react';
import React, { PureComponent } from 'react';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead

import { Subscribe } from 'unstated';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl-next';
import uuid from 'uuid/v4';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { token } from '@atlaskit/tokens';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { FlagsProvider } from '@atlaskit/flag';
import { Text } from '@atlaskit/primitives/compiled';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { DeleteNestedPagesActionLoader } from '@confluence/bulk-page-actions/entry-points/DeleteNestedPagesAction';
import {
	PRESENTER_MODE_VIEW_EXPERIENCE,
	DELETE_PAGE_EXPERIENCE,
	MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
	VIEW_PAGE_CONTENT_TOOLS_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceStart,
	withExperienceTracker,
} from '@confluence/experience-tracker/entry-points/component';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener';
import { ShortcutVisualizer, isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import {
	LINK_TO_THIS_PAGE_SHORTCUT,
	UNIVERSAL_LINK_TO_THIS_PAGE_SHORTCUT,
	PRESENTATION_MODE_SHORTCUT,
	UNIVERSAL_PRESENTATION_MODE_SHORTCUT,
	UNIVERSAL_PRESENTATION_MODE_SHORTCUT_SAFARI,
	VIEW_ATTACHMENTS_SHORTCUT,
	UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_MAC,
	UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { WebItemAnalyticsWrapper } from '@confluence/analytics/entry-points/WebItemAnalyticsWrapper';
import { fg } from '@confluence/feature-gating';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { ViewValues } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { ATTACHMENTS_ANALYTICS_FROM_ACTIONS_MENU } from '@confluence/attachments';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { pageAnalyticsUrlToRouteUrl } from '@confluence/confluence-analytics/entry-points/routeUtils';
import { CopyPageTreeDialog } from '@confluence/copy-page-tree-dialog';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import {
	ForgeKeyboardShortcut,
	ForgeKeyboardShortcutVisualizer,
} from '@confluence/forge-ui/entry-points/ForgeKeyboardShortcut';
import { LinkToThisPageDialog } from '@confluence/link-to-this-page-dialog';
import {
	SPACE_OVERVIEW,
	VIEW_PAGE,
	CUSTOM_CONTENTS_ON_PAGE_LIST,
	CONTENT_HISTORY,
} from '@confluence/named-routes/entry-points/routes';
import { FORCE_TRANSITION_QUERY_PARAM } from '@confluence/named-routes/entry-points/force-transition';
import { MovePageDialog } from '@confluence/page-tree/entry-points/MovePageDialog';
import { AnalyticsPluginWrapper } from '@confluence/plugin-wrappers';
import { LazyPresenterModeContextConsumer } from '@confluence/presentation-mode';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId';
import { PublishPageAsBlogDialog } from '@confluence/publish-page-as-blog/entry-points/PublishPageAsBlogDialog';
import { PublishPageAsBlogSources } from '@confluence/publish-page-as-blog/entry-points/PublishPageAsBlogSourcesEnum';
import { RevertEditorDialog } from '@confluence/editor-conversion/entry-points/RevertEditorDialog';
import { ResolvedInlineCommentsCounterLoader } from '@confluence/resolved-inline-comments-dialog/entry-points/ResolvedInlineCommentsCounter';
import { ResolvedInlineCommentsDialogLoader } from '@confluence/resolved-inline-comments-dialog/entry-points/ResolvedInlineCommentsDialog';
import { PublicLinksExportMenuEntryPoint } from '@confluence/public-links/entry-points/PublicLinksExportMenuEntryPoint';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { SessionData } from '@confluence/session-data/entry-points/SessionData';
import { withSessionData } from '@confluence/session-data/entry-points/withSessionData';
import {
	ANALYTICS_ADDON_ACTIONS_MENU_ID,
	ANALYTICS_MODERATE_ADDON_ACTIONS_MENU_ID,
} from '@confluence/web-item/entry-points/webItemIDs';
import { CONNECT_APP_PREFIX_KEY } from '@confluence/web-item/entry-points/webItemKeys';
import {
	type FormattedWebItem,
	WebItemLocation,
	type WebItemLocationChildrenFnWithLoading,
} from '@confluence/web-item-location/entry-points/WebItemLocation';
import { LoadableLazy } from '@confluence/loadable';
import { openForgeAppFromSubmenu } from '@confluence/legacy-keyboard-shortcut-helpers/entry-points/legacyKeyboardShortcutHelpers';
import { MoveBlogModalLoader } from '@confluence/move-blog-modal';
import {
	RevertAutoConvertEvent,
	AutoConvertActionSubject,
	sendEditorConversionAnalyticsPageEvent,
} from '@confluence/editor-conversion/entry-points/analytics';
import { ContentOwnerPopup } from '@confluence/content-ownership/entry-points/ContentOwnerPopupComponent';
import { CONTENT_OWNERSHIP_WEB_ITEM_ID } from '@confluence/content-ownership/entry-points/contentOwnershipConstants';
import { getSystemPreferredColorScheme } from '@confluence/theming-utils/entry-points/systemColorScheme';
import { withThemingState } from '@confluence/theming/entry-points/withThemingState';
import { CONVERT_PAGE_TO_FOLDER_WEBITEM_ID as ACTION_CONVERT_PAGE_TO_FOLDER_DIALOG } from '@confluence/folder-conversion/entry-points/useCanConvertPageToFolder';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { useSwitchToLivePage } from '@confluence/live-pages-features/entry-points/useSwitchToLivePage';
import { isSafari } from '@confluence/browser-helper';

import {
	ArchivePageItem,
	ACTION_ARCHIVE_PAGE_LINK,
	ACTION_RESTORE_PAGE_LINK,
} from './ArchivePageItem';
import {
	DELETE_PAGE_LINK,
	ACTION_MOVE_BLOG_DIALOG,
	REVERT_EDITOR_LINK,
	PAGE_HISTORY_LINK,
} from './constants';
import { ContentToolsComponent, type ContentToolsComponentProps } from './ContentToolsComponent';
import { ContentToolsButton } from './ContentToolsButton';
import type {
	ContentToolsClassProps,
	ContentToolsClassState,
	ContentToolsProps,
} from './ContentToolsProps';
import { ForgeContentTools } from './ForgeContentTools';
import { LazyContentToolsItem, LazyContentToolsItemWithPlaceholder } from './LazyContentToolsItem';
import { EndOfPageRecMenuItem } from './EndOfPageRecMenuItem';
import { ConvertToFolderItem } from './ConvertToFolderItem';
import {
	partitionWebItems,
	constructThirdPartyMenuItems,
	insertThirdPartySection,
	ThirdPartyAppsSubmenu,
	// eslint-disable-next-line import/no-cycle
} from './ThirdPartyAppsSubmenu';

const TOOLS_MENU_LOCATION = 'system.content.action';
const ACTION_MENU_RESOLVED_INLINE_COMMENTS = 'view-resolved-comments';
const ACTION_MENU_PDF_EXPORT = 'action-export-pdf-link';
const ACTION_MENU_WORD_EXPORT = 'action-export-word-link';
export const ACTION_MOVE_PAGE_DIALOG = 'action-move-page-dialog-link';
export const ACTION_EXPORT_WEB_ITEM = 'action-export-web-item';
export const ACTION_PUBLISH_PAGE_AS_BLOG_DIALOG = 'action-convert-page-to-blog';
export const VIEW_ATTACHMENTS_LINK = 'view-attachments-link';
export const VIEW_CUSTOM_CONTENTS_LINK = 'view-custom-contents-link';
const LINK_TO_THIS_PAGE = 'link-to-page-link';
export const LINK_TO_VIEW_SOURCE = 'action-view-source-link';
const LINK_TO_VIEW_HIERARCHY = 'view-in-hierarchy-link';
const LINK_TO_VIEW_STORAGE = 'action-view-storage-link';
const VIEW_CONTENT_API = 'content-api';
const IMPORT_WORD_DOC = 'import-word-doc';
const EXPORT = 'export';
const ADVANCED = 'advanced';
export const THIRD_PARTY = 'third-party';
const OTHERS = 'others';
const END_OF_PAGE_RECOMMENDATION_MENU = 'end-of-page-recommendation-menu';
const ATTACHMENTS = 'attachments';
export const SWITCH_TO_LIVE_EDIT_WEB_ITEM = 'action-switch-to-live-edit';
const CONTENT_OWNER = 'content-owner';

export const COPY_PAGE_LINK = 'action-copy-page-link';
const PAGE_INFO_LINK = 'view-page-info-link';
export const DELETE_BUTTON_ANALYTICS_SOURCE = 'contentToolsItem';
export const REVERT_BUTTON_ANALYTICS_SOURCE = 'contentToolsItem';
export const PRESENTATION_MODE = 'presentation-mode';

// NOTE: these same styles are used in MoreActions.tsx in @confluence/content-types-header
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelContainer = styled.div({
	flex: 2,
	marginRight: token('space.075'),
});

const LazyContentToolsItemForCopy = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ContentToolsItemForCopy" */ './ContentToolsItemForCopy'
			)
		).ContentToolsItemForCopy,
});

const LazyContentToolsItemForMove = LazyContentToolsItem;

const i18n = defineMessages({
	filesLabel: {
		defaultMessage: 'Files',
		id: 'content-tools.files.nested.section.heading.files',
		description:
			'The text for Files that display in the menu dropdown that group Files and Custom Contents',
	},
	customContentsLabel: {
		defaultMessage: 'Custom Contents',
		id: 'content-tools.files.nested.section.heading.custom-contents',
		description:
			'The text for Custom Contents that display in the menu dropdown that group Files and Custom Contents',
	},
});

class ContentToolsClass extends PureComponent<ContentToolsClassProps, ContentToolsClassState> {
	context: any;
	static propTypes = {
		spaceKey: PropTypes.string.isRequired,
		contentId: PropTypes.string.isRequired,
		createAnalyticsEvent: PropTypes.func.isRequired,
		version: PropTypes.number,
		onRender: PropTypes.func,
		intl: PropTypes.object,
		experienceTracker: PropTypes.object.isRequired,
		isFabricPage: PropTypes.bool,
		/**
		 * Used by Embedded Pages to specify the identifiers of allowed
		 * `ContentToolsItem`s. An emtpy array means that no `ContentToolsItem`s are
		 * allowed and, consequently, `ContentTools` will not render a menu at all.
		 * For Confluence all `ContentToolItem`s will be allowed!
		 */
		allowedContentToolsItems: PropTypes.arrayOf(PropTypes.string),
		contentType: PropTypes.string,
		isExternalCollaborator: PropTypes.bool,
		theme: PropTypes.shape({
			dark: PropTypes.string,
			light: PropTypes.string,
			colorMode: PropTypes.string,
		}),
		isContentToolsForcedOpen: PropTypes.bool,
		setIsContentToolsForcedOpen: PropTypes.func,
	};

	contentToolsComponentRef = React.createRef<{ closeDialog: () => void }>();

	splitInSections = (webItems: ReadonlyArray<FormattedWebItem>) =>
		webItems ? groupBy(webItems, 'section') : {};

	makePublishPageAsBlogDialogOpener = (dialogs) => () => {
		const { contentId, createAnalyticsEvent, spaceKey } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'publishPageAsBlog',
				source: 'contentToolsItem',
			},
		}).fire();
		dialogs.showDialog(PublishPageAsBlogDialog, {
			contentId,
			spaceKey,
			source: PublishPageAsBlogSources.ViewPage,
		});
	};

	makePresenterModeOpener = (enterPresenterMode) => () => {
		const { contentId, createAnalyticsEvent, experienceTracker, spaceId, theme } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'present',
				source: 'moreActions',
				objectId: contentId,
				containerId: spaceId,
				attributes: {
					currentThemePreference: theme,
					OStheme: getSystemPreferredColorScheme(),
				},
			},
		}).fire();
		experienceTracker.start({
			name: PRESENTER_MODE_VIEW_EXPERIENCE,
		});
		enterPresenterMode();
	};

	makeChangeOwnershipOpener = (openChangeOwnership) => () => {
		const { contentId, createAnalyticsEvent } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'page.owner',
				source: 'moreActions',
				objectId: contentId,
			},
		}).fire();
		openChangeOwnership();
	};

	makeDeletePageDialogOpener = (dialogs, edition) => () => {
		const { contentId, contentType, createAnalyticsEvent, experienceTracker } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'deletePage',
				source: DELETE_BUTTON_ANALYTICS_SOURCE,
				attributes: {
					showArchiveUpsell: edition === ConfluenceEdition.FREE,
				},
			},
		}).fire();
		experienceTracker.start({
			name: DELETE_PAGE_EXPERIENCE,
		});
		dialogs.showModal(DeleteNestedPagesActionLoader, {
			contentId,
			source: 'contentToolsItem',
			shouldRedirectOnSuccess: true,
			contentType,
		});
	};

	makeRevertEditorDialogOpener = (dialogs) => async () => {
		const { contentId, createAnalyticsEvent } = this.props;

		sendEditorConversionAnalyticsPageEvent(
			createAnalyticsEvent,
			{
				pageAction: RevertAutoConvertEvent.DIALOG_OPEN,
				source: 'ViewPageAutoConversion',
			},
			AutoConvertActionSubject.AUTO_CONVERSION,
		);
		dialogs.showDialog(RevertEditorDialog, { contentId });
	};

	makeLinkToThisPageDialogOpener = (dialogs) => () => {
		const { contentId } = this.props;

		dialogs.showDialog(LinkToThisPageDialog, { contentId });
	};

	makeCopyPageTreeDialogOpener = (dialogs) => () => {
		const { contentId, spaceKey } = this.props;

		dialogs.showDialog(CopyPageTreeDialog, { contentId, spaceKey });
	};

	makeInlineCommentsDialogOpener = (dialogs, showCommentsPanel, isFabricPage) => () => {
		if (isFabricPage && fg('confluence-frontend-comments-panel')) {
			showCommentsPanel({ openWithView: ViewValues.RESOLVED });
			return;
		}

		const { contentId, spaceKey } = this.props;

		dialogs.showDialog(ResolvedInlineCommentsDialogLoader, {
			spaceKey,
			contentId,
			context: 'renderer',
		});
	};

	makeModernizedMovePageDialogOpener = (dialogs) => () => {
		const { contentId, createAnalyticsEvent, experienceTracker, spaceKey } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'move',
				source: 'contentToolsItem',
				objectType: 'page',
			},
		}).fire();
		experienceTracker.start({
			name: MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
		});
		dialogs.showDialog(MovePageDialog, {
			spaceKey,
			contentId,
			referrer: 'view-page-actions-menu',
		});
	};

	makeModernizedMoveBlogModalOpener = (dialogs) => () => {
		const { contentId, createAnalyticsEvent, spaceKey } = this.props;

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'move',
				source: 'contentToolsItem',
				objectType: 'blog',
			},
		}).fire();
		dialogs.showModal(MoveBlogModalLoader, {
			currentSpaceKey: spaceKey,
			blogPostId: contentId,
			isEditMode: false,
			isUnpublishedDraft: false,
			referrer: 'view-page-actions-menu',
		});
	};

	navigateToAttachments = (push, url) => () => {
		push(url);
	};

	closeDropdownMenu = () => {
		this.setState({
			nestedPopup: undefined,
			isDropdownMenuOpen: false,
		});
	};
	getSectionComponent = (
		menuGroupings: ReadonlyArray<FormattedWebItem>,
		section: NonNullable<ContentToolsClassState['nestedPopup']>,
		dialogs: DialogsStateContainer,
		contentId,
		triggerLabel,
	) => {
		return (
			<DropdownMenu
				key={`${section}-dropdown`}
				placement="auto-start"
				isOpen={this.state?.nestedPopup === section}
				onOpenChange={({ isOpen }) => {
					if (isOpen) {
						this.setState({
							nestedPopup: section,
						});
					} else {
						this.setState({
							nestedPopup: undefined,
						});
					}
				}}
				trigger={({ triggerRef, ...triggerProps }) => (
					<DropdownItem
						{...triggerProps}
						ref={triggerRef}
						testId={`${section}-button`}
						elemAfter={<ChevronRightIcon label="" color={token('color.icon')} size="small" />}
					>
						{section === EXPORT &&
							// when backend supplies a label, use that instead.
							(triggerLabel ? (
								<Text>{triggerLabel}</Text>
							) : (
								<FormattedMessage
									defaultMessage="Export"
									id="content-tools.export.nested.section.heading"
									description="The text that display in the menu dropdown that group Export to PDF and Export to Word"
								/>
							))}
						{section === ADVANCED && (
							<FormattedMessage
								defaultMessage="Advanced details"
								id="content-tools.advanced.nested.section.heading"
								description="The text that display in the menu dropdown that group Page Information, Page Information, View Source and View Content API"
							/>
						)}
						{section === ATTACHMENTS && (
							<FormattedMessage
								defaultMessage="Attachments"
								id="content-tools.attachments.nested.section.heading"
								description="The text that display in the menu dropdown that group Files and Custom Contents"
							/>
						)}
					</DropdownItem>
				)}
				shouldRenderToParent={fg('should-render-to-parent-should-be-trueenterprise-e')}
			>
				<DropdownItemGroup>
					{menuGroupings.filter(Boolean).map((sectionItem) => {
						if (sectionItem.id === LINK_TO_THIS_PAGE) {
							sectionItem.url = '';
							sectionItem.urlWithoutContextPath = '';
							// analytics to monitor the roll out of removal of Link to this page from page actions menu
							return (
								<WebItemAnalyticsWrapper
									key={sectionItem.completeKey}
									analyticsData={{
										type: 'sendUIEvent',
										data: {
											action: 'clicked',
											actionSubject: 'LinkToThisPageButton',
											source: 'viewPageScreen',
											attributes: {
												triggeredFrom: 'actionsMenu',
											},
										},
									}}
								>
									<LazyContentToolsItem
										key={sectionItem.completeKey}
										{...sectionItem}
										onClick={this.makeLinkToThisPageDialogOpener(dialogs)}
									/>
								</WebItemAnalyticsWrapper>
							);
						} else if (sectionItem.id === LINK_TO_VIEW_HIERARCHY) {
							// analytics to monitor the roll out of removal of View In Hierarchy from page actions menu
							return (
								<WebItemAnalyticsWrapper
									key={sectionItem.completeKey}
									analyticsData={{
										type: 'sendUIEvent',
										data: {
											action: 'clicked',
											actionSubject: 'ViewInHierarchyButton',
											source: 'viewPageScreen',
											attributes: {
												triggeredFrom: 'actionsMenu',
											},
										},
									}}
								>
									<LazyContentToolsItem key={sectionItem.completeKey} {...sectionItem} />
								</WebItemAnalyticsWrapper>
							);
						} else if (sectionItem.id === END_OF_PAGE_RECOMMENDATION_MENU) {
							return (
								<EndOfPageRecMenuItem
									key={sectionItem.completeKey}
									entityId={contentId}
									entityType={this.props.contentType}
									spaceKey={this.props.spaceKey}
									contentToolsRef={this.contentToolsComponentRef.current}
								/>
							);
						} else if (sectionItem.id === VIEW_ATTACHMENTS_LINK) {
							// Renaming inner Attachments-label to "Files"
							const label = this.props.intl.formatMessage(i18n.filesLabel);
							const attachmentsCountStr = sectionItem?.label?.match(/\d+/g)?.[0] || '';
							const newLabel = `${label} (${attachmentsCountStr})`;

							return (
								<WebItemAnalyticsWrapper
									key={sectionItem.completeKey}
									analyticsData={ATTACHMENTS_ANALYTICS_FROM_ACTIONS_MENU}
								>
									<LazyContentToolsItem {...sectionItem} label={newLabel} />
								</WebItemAnalyticsWrapper>
							);
						} else if (sectionItem.id === VIEW_CUSTOM_CONTENTS_LINK) {
							// TODO: make a call (gql.content() when ready) to get numberOfCustomContentTypes
							const label = this.props.intl.formatMessage(i18n.customContentsLabel);
							const numStr = '';
							const newLabel = `${label} (${numStr})`;

							return (
								<WebItemAnalyticsWrapper
									key={sectionItem.completeKey}
									analyticsData={{
										type: 'sendUIEvent',
										data: {
											action: 'clicked',
											actionSubject: 'ViewCustomContentsButton',
											source: 'contentToolMenu',
											attributes: {
												triggeredFrom: 'actionsMenu',
											},
										},
									}}
								>
									<LazyContentToolsItem {...sectionItem} label={newLabel} />
								</WebItemAnalyticsWrapper>
							);
						}

						return <LazyContentToolsItem key={sectionItem.completeKey} {...sectionItem} />;
					})}
					{section === EXPORT && (
						<PublicLinksExportMenuEntryPoint
							closeDropdownMenu={this.closeDropdownMenu}
							contentId={contentId}
						/>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
		);
	};

	createMenuItems(
		webItems: ReadonlyArray<FormattedWebItem>,
		webSectionsKeys,
		dialogs,
		isSpaceOverview = false,
		isViewPage = true,
		isHistoryPage = false,
	) {
		const {
			contentId,
			isFabricPage,
			spaceKey,
			isContentToolsForcedOpen,
			switchToLivePage,
			showCommentsPanel,
			isLivePagesFeatureEnabled,
		} = this.props;

		const sections = this.splitInSections(webItems);
		const itemSections: Array<NonNullable<ReactNode>> = [];
		const keyboardShortcuts: ContentToolsComponentProps['keyboardShortcuts'] = []; //need support for web items
		const exportMenuGroupings: Array<FormattedWebItem> = [];
		const advancedMenuGroupings: Array<FormattedWebItem> = [];
		const thirdPartyMenuGroupings: Array<FormattedWebItem> = [];
		const attachmentMenuGroupings: Array<FormattedWebItem> = [];
		const commonProps = {};
		let revertEditorComponent;

		// First iteration: popuplate menu groupings arrays.
		(webSectionsKeys || Object.keys(sections)).forEach((key) => {
			(sections[key] || []).forEach((sectionItem): ReactNode => {
				switch (sectionItem['subSection']) {
					case EXPORT:
						exportMenuGroupings.push(sectionItem);
						return;
					case ADVANCED:
						advancedMenuGroupings.push(sectionItem);
						return;
					case THIRD_PARTY:
						// Necessary for some macros/apps especially for third party Connect macro/apps to render iframes/modals
						// for some reason ¯\_(ツ)_/¯
						sectionItem['className'] = sectionItem.styleClass;
						thirdPartyMenuGroupings.push(sectionItem);
						return;
				}
			});
		});

		// Second iteration: generate JSX to render
		(webSectionsKeys || Object.keys(sections)).forEach((key) => {
			const sectionItemsComponent = (sections[key] || [])
				.map((sectionItem): ReactNode => {
					// Skip over any `sectionItem`s that were already adding to a MenuGrouping above.
					switch (sectionItem['subSection']) {
						case EXPORT:
							return null;
						case ADVANCED:
							return null;
						case THIRD_PARTY:
							return null;
					}
					if (sectionItem.id === REVERT_EDITOR_LINK) {
						//need to check if it is available and then push to the array at the end to put it at the bottom after third party section.
						revertEditorComponent = (
							<ErrorBoundary attribution={Attribution.BACKBONE} key={sectionItem.completeKey}>
								<LazyContentToolsItem
									key={sectionItem.completeKey}
									{...sectionItem}
									{...commonProps}
									url={undefined}
									onClick={this.makeRevertEditorDialogOpener(dialogs)}
								/>
							</ErrorBoundary>
						);
						return null;
					}

					if (sectionItem.id === PAGE_HISTORY_LINK) {
						// Link component ignores query params so it still transitions instead of going to Next
						return (
							<LazyContentToolsItem
								{...commonProps}
								id={sectionItem.id}
								label={sectionItem.label}
								url={sectionItem.url}
								key={sectionItem.completeKey}
							/>
						);
					}

					if (
						sectionItem.id === ANALYTICS_ADDON_ACTIONS_MENU_ID ||
						sectionItem.id === ANALYTICS_MODERATE_ADDON_ACTIONS_MENU_ID
					) {
						// Replace the WebItem URL with the new native UI route URL
						const url = pageAnalyticsUrlToRouteUrl(sectionItem.url);

						return (
							<AnalyticsPluginWrapper webItemId={sectionItem.id} key={sectionItem.completeKey}>
								<LazyContentToolsItem {...sectionItem} url={url} {...commonProps} />
							</AnalyticsPluginWrapper>
						);
					}

					if (sectionItem.id === ACTION_MENU_RESOLVED_INLINE_COMMENTS) {
						return (
							<LazyContentToolsItem
								{...commonProps}
								id="bm-resolved-inline-comments"
								key={sectionItem.completeKey}
								label={
									<Container>
										<LabelContainer>
											<Text>{sectionItem.label} </Text>
										</LabelContainer>
										<ResolvedInlineCommentsCounterLoader contentId={contentId} />
									</Container>
								}
								onClick={this.makeInlineCommentsDialogOpener(
									dialogs,
									showCommentsPanel,
									isFabricPage,
								)}
							/>
						);
					}

					if (sectionItem.id === LINK_TO_THIS_PAGE) {
						sectionItem.url = '';
						sectionItem.urlWithoutContextPath = '';
						keyboardShortcuts.push({
							accelerator: UNIVERSAL_LINK_TO_THIS_PAGE_SHORTCUT,
							deprecatedAccelerator: LINK_TO_THIS_PAGE_SHORTCUT,
							itemId: sectionItem.id,
						});

						return (
							<LazyContentToolsItem
								key={sectionItem.completeKey}
								{...sectionItem}
								{...commonProps}
								onClick={this.makeLinkToThisPageDialogOpener(dialogs)}
							/>
						);
					}

					if (sectionItem.id === COPY_PAGE_LINK) {
						sectionItem.url = '';
						sectionItem.urlWithoutContextPath = '';

						return (
							<LazyContentToolsItemForCopy
								key={sectionItem.completeKey}
								{...sectionItem}
								{...commonProps}
								onClick={this.makeCopyPageTreeDialogOpener(dialogs)}
							/>
						);
					}

					if (sectionItem.id === PRESENTATION_MODE) {
						if (!isSpaceOverview && !isHistoryPage) {
							sectionItem.url = '';
							sectionItem.urlWithoutContextPath = '';
							keyboardShortcuts.push({
								accelerator: isSafari()
									? UNIVERSAL_PRESENTATION_MODE_SHORTCUT_SAFARI
									: UNIVERSAL_PRESENTATION_MODE_SHORTCUT,
								deprecatedAccelerator: PRESENTATION_MODE_SHORTCUT,
								itemId: sectionItem.id,
							});

							return (
								<ErrorBoundary attribution={Attribution.COMMENTS} key={sectionItem.completeKey}>
									<LazyPresenterModeContextConsumer
										key={`presenter-mode-menu-item-${sectionItem.completeKey}`}
									>
										{(enterPresenterMode) => (
											<LazyContentToolsItemWithPlaceholder
												key={sectionItem.completeKey}
												{...sectionItem}
												{...commonProps}
												onClick={this.makePresenterModeOpener(enterPresenterMode)}
												label={
													<Container>
														<LabelContainer>
															<Text>{sectionItem.label}</Text>
														</LabelContainer>
														<ShortcutVisualizer
															shortcut={
																isSafari()
																	? UNIVERSAL_PRESENTATION_MODE_SHORTCUT_SAFARI
																	: UNIVERSAL_PRESENTATION_MODE_SHORTCUT
															}
														/>
													</Container>
												}
											/>
										)}
									</LazyPresenterModeContextConsumer>
								</ErrorBoundary>
							);
						}
						return null;
					}

					if (sectionItem.id === CONTENT_OWNERSHIP_WEB_ITEM_ID) {
						if (isViewPage) {
							sectionItem.url = '';
							sectionItem.urlWithoutContextPath = '';

							const handleOnClick = () => {
								if (this.state?.nestedPopup === CONTENT_OWNER) {
									this.setState({
										nestedPopup: undefined,
									});
								} else {
									this.setState({
										nestedPopup: CONTENT_OWNER,
									});
								}
							};

							const handleClosePopup = () => {
								this.setState({
									nestedPopup: undefined,
								});
							};

							return (
								<ErrorBoundary attribution={Attribution.BACKBONE} key={sectionItem.completeKey}>
									<ContentOwnerPopup
										key={`container-${sectionItem.id}`}
										itemId={sectionItem.id}
										contentId={contentId}
										itemLabel={sectionItem.label}
										onCloseParent={this.closeDropdownMenu}
										onClosePopup={handleClosePopup}
										isOpen={this.state?.nestedPopup === CONTENT_OWNER}
										onClick={handleOnClick}
									/>
								</ErrorBoundary>
							);
						}
						return null;
					}

					if (sectionItem.id === ACTION_PUBLISH_PAGE_AS_BLOG_DIALOG) {
						if (isViewPage && isFabricPage) {
							sectionItem.url = '';
							sectionItem.urlWithoutContextPath = '';

							return (
								<LazyContentToolsItem
									key={sectionItem.completeKey}
									{...sectionItem}
									{...commonProps}
									onClick={this.makePublishPageAsBlogDialogOpener(dialogs)}
								/>
							);
						}
						return null;
					}

					if (sectionItem.id === ACTION_CONVERT_PAGE_TO_FOLDER_DIALOG) {
						if (isViewPage) {
							sectionItem.url = '';
							sectionItem.urlWithoutContextPath = '';

							return (
								<ConvertToFolderItem
									key={sectionItem.completeKey}
									sectionItem={sectionItem}
									contentId={contentId}
									spaceKey={spaceKey}
									dialogs={dialogs}
									// should highlight button when content tools is opened by folder conversion banner
									shouldHighlight={isContentToolsForcedOpen}
									{...commonProps}
								/>
							);
						}
						return null;
					}

					if (sectionItem.id === VIEW_ATTACHMENTS_LINK) {
						const sectionItemForCustomContents = {
							id: VIEW_CUSTOM_CONTENTS_LINK,
							// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
							label: 'Custom Contents',
							url: CUSTOM_CONTENTS_ON_PAGE_LIST.toUrl({
								contentId,
								spaceKey,
							}),
							accessKey: null,
							completeKey: 'confluence.content.action.menu.new:view-custom-contents',
							hasCondition: true,
							icon: null,
							subSection: ATTACHMENTS,
						} as any;

						attachmentMenuGroupings.push(sectionItem);
						attachmentMenuGroupings.push(sectionItemForCustomContents);

						return this.getSectionComponent(
							attachmentMenuGroupings,
							ATTACHMENTS,
							dialogs,
							contentId,
							sectionItem.label,
						);
					}

					sectionItem['className'] = sectionItem.styleClass;

					if (sectionItem.id === DELETE_PAGE_LINK) {
						return (
							<ErrorBoundary
								attribution={Attribution.ADMIN_EXPERIENCE}
								key={sectionItem.completeKey}
							>
								<SessionData>
									{({ edition }) => (
										<LazyContentToolsItem
											key={sectionItem.completeKey}
											{...sectionItem}
											{...commonProps}
											url={undefined}
											onClick={this.makeDeletePageDialogOpener(dialogs, edition)}
										/>
									)}
								</SessionData>
							</ErrorBoundary>
						);
					}

					if (
						sectionItem.id === ACTION_ARCHIVE_PAGE_LINK ||
						sectionItem.id === ACTION_RESTORE_PAGE_LINK
					) {
						return (
							<ArchivePageItem
								key={sectionItem.completeKey}
								{...commonProps}
								contentId={contentId}
								sectionItem={sectionItem}
							/>
						);
					}

					if (
						sectionItem.id === ACTION_MOVE_BLOG_DIALOG ||
						sectionItem.id === ACTION_MOVE_PAGE_DIALOG
					) {
						const makeDialogOpener: keyof ContentToolsClass =
							sectionItem.id === ACTION_MOVE_BLOG_DIALOG
								? 'makeModernizedMoveBlogModalOpener'
								: 'makeModernizedMovePageDialogOpener';

						sectionItem.url = '';
						sectionItem.urlWithoutContextPath = '';
						sectionItem.id = `${sectionItem.id}-modernized`;

						return (
							<LazyContentToolsItemForMove
								key={sectionItem.completeKey}
								{...sectionItem}
								{...commonProps}
								onClick={this[makeDialogOpener](dialogs)}
							/>
						);
					}

					if (sectionItem.id === SWITCH_TO_LIVE_EDIT_WEB_ITEM) {
						if (!isHistoryPage && isLivePagesFeatureEnabled) {
							sectionItem.url = '';
							sectionItem.urlWithoutContextPath = '';
							return (
								<LazyContentToolsItem
									key={sectionItem.completeKey}
									{...sectionItem}
									{...commonProps}
									onClick={switchToLivePage}
								/>
							);
						}
						return null;
					}

					if (sectionItem.id === ACTION_EXPORT_WEB_ITEM) {
						if (key === 'system.content.action/modify' && !this.props.allowedContentToolsItems) {
							return this.getSectionComponent(
								exportMenuGroupings,
								EXPORT,
								dialogs,
								contentId,
								sectionItem.label,
							);
						}
						return null;
					}

					return <LazyContentToolsItem key={sectionItem.completeKey} {...sectionItem} />;
				})
				.filter((item): item is NonNullable<ReactNode> => !!item);

			if (key === 'system.content.action/primary' && !this.props.allowedContentToolsItems) {
				const inlineCommentsMenuItemIndex = sectionItemsComponent.findIndex(
					(sectionItem) =>
						sectionItem?.['key'] ===
						'com.atlassian.confluence.plugins.confluence-inline-comments:view-resolved-comments-new',
				);

				const advancedSectionComponent = this.getSectionComponent(
					advancedMenuGroupings,
					ADVANCED,
					dialogs,
					contentId,
					undefined,
				);
				sectionItemsComponent.splice(inlineCommentsMenuItemIndex + 1, 0, advancedSectionComponent);
			}

			if (sectionItemsComponent.length > 0) {
				itemSections.push(sectionItemsComponent);
			}
		});

		if (!this.props.allowedContentToolsItems) {
			// reorder Slack Notification to the beginning of the list
			const slackIntegrationMenuItemIndex = thirdPartyMenuGroupings.findIndex(
				(item) => item.id === 'confluence-chats-integration__page-config-dialog-trigger',
			);
			const slackIntegrationMenuItem = thirdPartyMenuGroupings.splice(
				slackIntegrationMenuItemIndex,
				1,
			)[0];
			thirdPartyMenuGroupings.splice(0, 0, slackIntegrationMenuItem);

			const thirdPartyMenuGroupingsComponent = thirdPartyMenuGroupings
				.filter(Boolean)
				.map((item) => (
					<LazyContentToolsItemWithPlaceholder key={item.completeKey} {...item} {...commonProps} />
				));

			if (thirdPartyMenuGroupingsComponent.length > 0) {
				itemSections.push(thirdPartyMenuGroupingsComponent);
			}
		}

		if (revertEditorComponent) {
			itemSections.push(revertEditorComponent);
		}

		return { itemSections, keyboardShortcuts };
	}

	groupWebItemsBySubSection = (webItems: ReadonlyArray<FormattedWebItem>) => {
		webItems.forEach((webItem) => {
			if ([ACTION_MENU_WORD_EXPORT, ACTION_MENU_PDF_EXPORT].indexOf(webItem.id!) !== -1) {
				webItem['subSection'] = EXPORT;
			} else if (
				[
					LINK_TO_THIS_PAGE,
					PAGE_INFO_LINK,
					LINK_TO_VIEW_SOURCE,
					LINK_TO_VIEW_HIERARCHY,
					LINK_TO_VIEW_STORAGE,
					VIEW_CONTENT_API,
					IMPORT_WORD_DOC,
					END_OF_PAGE_RECOMMENDATION_MENU,
				].indexOf(webItem.id!) !== -1
			) {
				webItem['subSection'] = ADVANCED;
			} else if (
				(webItem.completeKey ?? '').indexOf(CONNECT_APP_PREFIX_KEY) !== -1 &&
				webItem.id !== ANALYTICS_ADDON_ACTIONS_MENU_ID &&
				webItem.id !== ANALYTICS_MODERATE_ADDON_ACTIONS_MENU_ID
			) {
				webItem['subSection'] = THIRD_PARTY;
			} else if ([ATTACHMENTS, VIEW_CUSTOM_CONTENTS_LINK].indexOf(webItem.id!) !== -1) {
				webItem['subSection'] = ATTACHMENTS;
			} else {
				webItem['subSection'] = OTHERS;
			}
		});
	};

	getAllowedWebItems = (webItems: ReadonlyArray<FormattedWebItem>) => {
		if (this.props.allowedContentToolsItems) {
			return webItems.filter((sectionItem) =>
				this.props.allowedContentToolsItems!.includes(sectionItem.id!),
			);
		}
		return webItems;
	};

	addEndofPageRecommendationToWebItems = (webItems) => [
		...webItems,
		{
			accessKey: null,
			completeKey: END_OF_PAGE_RECOMMENDATION_MENU,
			hasCondition: true,
			icon: null,
			id: END_OF_PAGE_RECOMMENDATION_MENU,
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-object
			label: 'Hide related pages...',
			moduleKey: 'end-of-page-recommendation-menu',
			section: 'system.content.action/primary',
			webSection: 'secondary',
		},
	];

	renderMenuItems = ({ contentId, dialogs, routesContext: { match, push } }) => (
		<WebItemLocation
			tagName="span"
			location={TOOLS_MENU_LOCATION}
			hasMultipleSections
			contentId={contentId}
			version={this.props.version ?? undefined}
			renderWhenLoading
		>
			{
				(({ loading, webItems, webSections }) => {
					const {
						contentType,
						isExternalCollaborator,
						spaceKey,
						isContentToolsForcedOpen,
						setIsContentToolsForcedOpen,
					} = this.props;

					if (loading) {
						if (this.props.allowedContentToolsItems?.length == 0) {
							return null;
						}

						// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
						const isPageSSRd = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);

						return (
							<>
								<ContentToolsButton isDisabled={!isPageSSRd} />
							</>
						);
					}

					webItems = this.addEndofPageRecommendationToWebItems(webItems);

					const allowedWebItems = this.getAllowedWebItems(webItems);

					this.groupWebItemsBySubSection(allowedWebItems);

					const isSpaceOverview = match?.name === SPACE_OVERVIEW.name;
					const isViewPage = match?.name === VIEW_PAGE.name;
					const isHistoryPage = match?.name === CONTENT_HISTORY.name;

					let thirdPartyWebItems: FormattedWebItem[] = [];
					const partitionResult = partitionWebItems(allowedWebItems);
					thirdPartyWebItems = partitionResult.thirdParty;
					const createMenuItemsResult = this.createMenuItems(
						partitionResult.firstParty,
						webSections,
						dialogs,
						isSpaceOverview,
						isViewPage,
						isHistoryPage,
					);
					const { itemSections, keyboardShortcuts } = createMenuItemsResult;

					/* For EP View Page when item-sections is empty do not render content tools */
					if (this.props.allowedContentToolsItems && itemSections.length == 0) {
						return null;
					}

					return (
						<ForgeContentTools
							spaceKey={spaceKey}
							contentId={contentId}
							onLoadComplete={() => {
								this.contentToolsComponentRef.current?.closeDialog();
							}}
							isDropdownMenuOpen={this.state?.isDropdownMenuOpen ?? false}
						>
							{(forgeMenuItems) => {
								let result = itemSections;
								let thirdPartyComponentList: React.ReactElement[] = [];
								if (forgeMenuItems.length) {
									const tooltippedForgeMenuItems = forgeMenuItems.map((f) => (
										<ForgeKeyboardShortcutVisualizer
											key={f.props.extension.id}
											module={f.props.extension}
										>
											{f}
										</ForgeKeyboardShortcutVisualizer>
									));

									thirdPartyComponentList = [
										...constructThirdPartyMenuItems(thirdPartyWebItems),
										...tooltippedForgeMenuItems,
									];
								} else {
									// If there aren't any Forge apps, combine the non-Forge
									// third party apps into a single list of components.
									thirdPartyComponentList = constructThirdPartyMenuItems(thirdPartyWebItems);
								}

								// If there are third party components, which implies that the feature,
								// we turn the third party components into a nested "Apps" menu and insert
								// it into the full menu, ideally splicing it to appear before the section
								// that contains the "Page history" component, as is done for live pages.
								if (thirdPartyComponentList.length) {
									const thirdPartySection = (
										<ThirdPartyAppsSubmenu
											nestedPopup={this.state?.nestedPopup}
											onSetPopup={(popup) => this.setState({ nestedPopup: popup })}
											menuItems={thirdPartyComponentList}
										/>
									);
									result = insertThirdPartySection(thirdPartySection, result);
								}

								const forgeKeyboardShortcuts = forgeMenuItems.map((f) => (
									<ForgeKeyboardShortcut
										key={f.props.extension.id}
										module={f.props.extension}
										action={() => {
											void openForgeAppFromSubmenu({
												parentmenuId: '#tools-menu-trigger',
												submenuId: "[data-testid='third-party-button']",
												forgeItemId: f.props.extension.id,
											});
										}}
									/>
								));
								const viewAttachmentsWebItem = webItems.find(
									(webItem) => webItem.id === VIEW_ATTACHMENTS_LINK,
								);
								const viewAttachmentsShotcutListener = viewAttachmentsWebItem ? (
									<GeneralShortcutListener
										deprecatedAccelerator={VIEW_ATTACHMENTS_SHORTCUT}
										accelerator={
											isMac()
												? UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_MAC
												: UNIVERSAL_VIEW_ATTACHMENTS_SHORTCUT_WIN
										}
										listener={this.navigateToAttachments(push, viewAttachmentsWebItem.url)}
									/>
								) : null;

								const isMenuOpen =
									!!this.state?.isDropdownMenuOpen || isContentToolsForcedOpen || false;

								return (
									<>
										<ContentToolsComponent
											contentType={contentType}
											key="content-tools-component"
											contentId={contentId}
											itemSections={result}
											keyboardShortcuts={keyboardShortcuts}
											// @ts-ignore
											ref={this.contentToolsComponentRef}
											isMenuOpen={isMenuOpen}
											onOpenChange={(isOpen) => {
												if (!this.state?.nestedPopup) {
													this.setState({
														isDropdownMenuOpen: isOpen,
													});
													if (isContentToolsForcedOpen && !isOpen) {
														setIsContentToolsForcedOpen && setIsContentToolsForcedOpen(false);
													}
												}
											}}
											spaceKey={spaceKey}
											isExternalCollaborator={isExternalCollaborator}
										/>
										{forgeKeyboardShortcuts}
										{viewAttachmentsShotcutListener}
									</>
								);
							}}
						</ForgeContentTools>
					);
				}) as WebItemLocationChildrenFnWithLoading
			}
		</WebItemLocation>
	);

	render() {
		const { contentId, onRender } = this.props;
		onRender?.();
		return (
			<FlagsProvider>
				<ErrorBoundary attribution={Attribution.BACKBONE}>
					<ExperienceStart id={contentId} name={VIEW_PAGE_CONTENT_TOOLS_EXPERIENCE} />
					<Subscribe to={[DialogsStateContainer]}>
						{(dialogs: DialogsStateContainer) => (
							<>
								<RoutesContext.Consumer>
									{(routesContext) => (
										<>
											{this.renderMenuItems({
												contentId,
												dialogs,
												routesContext,
											})}
										</>
									)}
								</RoutesContext.Consumer>

								<GeneralShortcutListener
									deprecatedAccelerator={LINK_TO_THIS_PAGE_SHORTCUT}
									accelerator={UNIVERSAL_LINK_TO_THIS_PAGE_SHORTCUT}
									listener={this.makeLinkToThisPageDialogOpener(dialogs)}
								/>
							</>
						)}
					</Subscribe>
				</ErrorBoundary>
			</FlagsProvider>
		);
	}
}

const withHooks = (Component: any) => {
	return (props: ContentToolsProps & WithFlagsProps) => {
		const spaceId = useSpaceId();
		const { showCommentsPanel } = useShowCommentsPanel();
		const { push } = useRouteActions();
		const switchToLivePage = useSwitchToLivePage({
			contentId: props.contentId,
			spaceId,
			// Since this component is only used on the view page, isUnpublishedDraft must be false.
			isUnpublishedDraft: false,
			analyticsSource: 'viewPageScreen',
			adfOrigin: 'RENDERER',
			onSuccessfulConversion: () => {
				push(
					VIEW_PAGE.toUrl(
						{
							spaceKey: props.spaceKey,
							contentId: props.contentId,
						},
						// do not remove this query param. It is used to force a transition since the route is the same
						// and we need to force a transition to avoid using SSR data.
						{ query: { [FORCE_TRANSITION_QUERY_PARAM]: uuid() } },
					),
				);
			},
			flags: props.flags,
			retrieveEditorTitleAndContent: undefined,
		});
		const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();

		return (
			<Component
				spaceId={spaceId}
				switchToLivePage={switchToLivePage}
				showCommentsPanel={showCommentsPanel}
				isLivePagesFeatureEnabled={isLivePagesFeatureEnabled}
				{...props}
			/>
		);
	};
};

export const ContentTools = withFlags(
	withHooks(
		withSessionData(
			withExperienceTracker(
				// @ts-ignore
				withAnalyticsEvents()(withThemingState(injectIntl(ContentToolsClass))),
			),
		),
	),
) as ComponentType<ContentToolsProps>;
